"use client";

import React, { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { cn } from "@/lib/utils";
import { trackBetLinkClick, phCapture } from "@/utils/analytics";
import { ExternalLink } from "lucide-react";
import {
  type MarketOpportunity,
  type PeerOdds,
  type DepthHighlightMode,
  formatOdds,
  formatCurrency,
  formatClassicSideName,
  capitalizeMarket,
  formatClassicMarketTitle,
  getFairOutcomeShare,
  getBookDisplayName,
  getSideBookSelectionId,
  getWhaleVolumeDisplay,
  buildDepthLevelsForSelectedSide,
  buildPeerRows,
  getFairProbabilityForSide1,
  computeDepthStats,
  computeDepthLevelHighlighting,
  getWhaleWatchTrackingProps,
  getClassicBookAvailableLiquidity,
  formatClassicSportAndLeague,
  getPlayEntityName,
  getPlayLine,
} from "@/lib/whale-watch/whale-watch-shared";
import {
  canOpenBookHistory,
  findBookSeries,
  useEventLineHistory,
} from "@/components/odds/line-sparkline";
import {
  canAnchorOddsHistory,
  type OddsHistoryModalParams,
  type OddsHistoryPeerBook,
  type OddsHistoryQuoteCapacity,
  type OddsHistoryQuoteContext,
  type OddsHistorySelection,
} from "@/components/odds/odds-history-selection";
import { OddsHistoryModal } from "@/components/odds/lazy-odds-history-modal";
import { toBetLinkSet } from "@/lib/bet-links";
import { OddsHistoryTrigger } from "@/components/odds/odds-history-trigger";
import { EdgeBarDivider, EdgeBarRow, ToolSectionHeader } from "@/components/tools/edge-bars";
import { WhaleWatchBookLogo as BookLogo } from "@/components/whale-watch/whale-watch-book-logo";
import { BookLiquidityLabel } from "./peer-odds-cell";
import { WhereToBetTable } from "./where-to-bet-table";
import { OddsBadge } from "@/components/ui/odds-badge";
import { ShareButton } from "@/components/slip/share-button";
import { renderNothingIfChunkFails } from "@/lib/optional-chunk";
import { everywhere } from "@/lib/book-location";

const WhaleShareModal = dynamic(
  () =>
    renderNothingIfChunkFails(
      import("@/components/whale-watch/share-modal").then((mod) => mod.WhaleShareModal),
    ),
  { ssr: false },
);

function DepthBar({
  odds,
  liquidity,
  maxLiquidity,
  book,
  highlightMode = "none",
}: {
  odds: number;
  liquidity: number;
  maxLiquidity: number;
  book: string;
  highlightMode?: DepthHighlightMode;
}) {
  const isHighlighted = highlightMode !== "none";

  return (
    <EdgeBarRow
      label={<BookLogo book={book} size="sm" />}
      monoValue={formatOdds(odds)}
      value={liquidity}
      max={maxLiquidity}
      variant={highlightMode}
      styleSet="desktop"
      trailingValue={formatCurrency(liquidity, false)}
      className="flex items-center gap-3 py-1.5 group"
      monoValueClassName={cn(
        "w-11 text-sm tabular-nums text-right font-medium transition-colors",
        isHighlighted ? "text-zinc-300" : "text-zinc-500"
      )}
      barContainerClassName="flex-1 h-5 rounded overflow-hidden relative transition-all"
      barFillClassName="h-full rounded transition-all"
      trailingValueClassName={cn(
        "w-14 text-sm tabular-nums text-right font-medium transition-colors",
        isHighlighted ? "text-zinc-200" : "text-zinc-500"
      )}
    />
  );
}


export const DetailPanel = React.memo(function DetailPanel({ opp, selectedBookKeys, practice = false, onPracticeBet }: { opp: MarketOpportunity<PeerOdds> | null; selectedBookKeys: string[]; practice?: boolean; onPracticeBet?: () => void }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [historyModal, setHistoryModal] = useState<OddsHistoryModalParams | null>(null);

  const selectionKeys = useMemo(() => {
    const m = opp?.market;
    const s1 = m?.sides?.[0];
    const s2 = m?.sides?.[1];
    if (!s1 || !s2) return [];
    const sideLc = s1.side.toLowerCase();
    const ids = new Set<string>();
    const bestId = getSideBookSelectionId(s1, s1.best_book);
    if (bestId) ids.add(bestId);
    for (const r of buildPeerRows(s1, s2, everywhere, 50, m?.third_side)) {
      if (r.side1SelectionId) ids.add(r.side1SelectionId);
    }
    return [...ids].map((id) => `${id}|${sideLc}`);
  }, [opp]);
  const lineHistory = useEventLineHistory(
    opp?.event_id ?? "",
    Boolean(opp) && !practice,
    opp?.event_start ? new Date(opp.event_start) : null,
    selectionKeys,
  );

  if (!opp) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Select an opportunity to view details
      </div>
    );
  }

  const market = opp.market;
  if (!market || market.sides.length < 2) return null;

  const [side1, side2] = market.sides;

  const fairShare = getFairOutcomeShare(market);
  const thirdSide = market.third_side;
  const topDepthData = buildDepthLevelsForSelectedSide(side2, side1.best_book, 10, fairShare);
  const { maxLiquidity, totalLiquidity: totalDepthLiquidity, avgOdds } = computeDepthStats(topDepthData);
  const peerRows = buildPeerRows(side1, side2, everywhere, 50, thirdSide);
  const fairProbFor = (book: string) => getFairProbabilityForSide1(side2, book, fairShare);
  const fairProbSide1 = fairProbFor(side1.best_book);

  const marketLabel = formatClassicMarketTitle(market.market, market.market_arity);
  const historyFair =
    fairProbSide1 > 0 && fairProbSide1 < 1
      ? ({ display: "probability", probability: fairProbSide1 } as const)
      : undefined;
  const historyValue =
    side1.edge_percent != null && Number.isFinite(side1.edge_percent)
      ? ({ kind: "probability_edge", percent: side1.edge_percent } as const)
      : undefined;
  const firstCapacity = (
    ...candidates: Array<[OddsHistoryQuoteCapacity["kind"], number | undefined]>
  ): OddsHistoryQuoteCapacity | undefined => {
    for (const [kind, amount] of candidates) {
      if (amount != null && Number.isFinite(amount) && amount > 0) {
        return { kind, amount };
      }
    }
    return undefined;
  };
  const side1Capacity = (book: string): OddsHistoryQuoteCapacity | undefined =>
    firstCapacity(
      ["limit", side1.peers.find((p) => p.book === book)?.limit],
      ["liquidity", side1.books?.[book]?.total_liquidity],
      ["liquidity", side1.books?.[book]?.liquidity],
    );
  const side1PeerBooks = (excludeBook: string): OddsHistoryPeerBook[] =>
    peerRows.flatMap((r) =>
      r.side1Price && r.book !== excludeBook
        ? [{
            book: r.book,
            bookDisplayName: getBookDisplayName(r.book),
            ...(r.side1SelectionId ? { selectionId: r.side1SelectionId } : {}),
            line: getPlayLine(side1, market.market),
            oddsAmerican: r.side1Price.odds,
            betLinks: toBetLinkSet(r.side1BetLink, undefined, undefined) ?? undefined,
            capacity: side1Capacity(r.book),
            isBestPrice: r.side1Price.odds === side1.best_odds ? (true as const) : undefined,
          }]
        : [],
    );
  const historySelectionFor = (
    book: string,
    selectionId: string | undefined,
    quote: OddsHistoryQuoteContext | undefined,
  ): OddsHistorySelection => ({
    eventId: opp.event_id,
    book,
    selectionId,
    side: side1.side,
    line: getPlayLine(side1, market.market),
    market: market.market,
    marketLabel,
    entity: getPlayEntityName(opp, side1),
    eventStart: opp.event_start,
    matchup: opp.event,
    context: { fair: historyFair, value: historyValue, quote },
  });
  const openHistoryFor = (
    book: string,
    selectionId: string | undefined,
    surface: "desktop_detail" | "desktop_peer_grid",
    quote: OddsHistoryQuoteContext | undefined,
  ): (() => void) | undefined => {
    const selection = historySelectionFor(book, selectionId, quote);
    const series = findBookSeries(lineHistory, { book, selectionId, side: side1.side });
    if (
      !canAnchorOddsHistory(selection) ||
      !canOpenBookHistory(series, { book, selectionId, side: side1.side })
    ) {
      return undefined;
    }
    return () => {
      setHistoryModal({ selection, fallbackSeries: series, peers: side1PeerBooks(book) });
      phCapture("whale_watch_line_history_opened", { book, surface });
    };
  };
  const playLiquidity = getClassicBookAvailableLiquidity(side1, side1.best_book);
  const bestPeer = side1.peers.find((p) => p.book === side1.best_book) ?? null;
  const bestBookData = side1.books?.[side1.best_book] ?? null;
  const openPlayHistory = openHistoryFor(
    side1.best_book,
    getSideBookSelectionId(side1, side1.best_book),
    "desktop_detail",
    {
      oddsAmerican: Number.isFinite(side1.best_odds) ? side1.best_odds : undefined,
      betLinks:
        toBetLinkSet(
          side1.best_bet_link ?? bestPeer?.bet_link ?? bestBookData?.bet_link,
          undefined,
          undefined,
        ) ?? undefined,
      capacity: side1Capacity(side1.best_book),
      isBestPrice: true,
    },
  );

  return (
    <div className="h-full flex flex-col bg-surface-raised ring-1 ring-white/5 shadow-2xl relative z-0">
      <div key={opp.id} className="flex-1 flex flex-col animate-in fade-in slide-in-from-right-4 duration-300 ease-out fill-mode-both overflow-hidden">
        <div className="p-5 border-b border-white/[0.08] shrink-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              {formatClassicSportAndLeague(opp.sport, opp.league)}
            </span>
            {!practice && <ShareButton onClick={() => setShareOpen(true)} size="md" className="sm:opacity-100" />}
          </div>
          <h2 className="text-lg font-bold text-foreground mb-2">{opp.event}</h2>
          <div className="flex items-baseline gap-2">
            <span className="data-hero text-3xl text-accent-value text-glow-value">
              {formatCurrency(getWhaleVolumeDisplay(market).value, true)}
            </span>
            <span className="text-sm font-medium text-muted-foreground">Whale Volume</span>
          </div>
        </div>

        <div data-practice-target={practice ? "whale-play" : undefined} className="px-5 py-4 border-b border-white/[0.08] shrink-0">
          <div className="bg-surface-raised rounded-panel p-4 ring-1 ring-accent-value/20 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.4)] surface-toplight">
            <div data-whale-play-card={practice ? "true" : undefined} className="flex items-center gap-4">
              <BookLogo book={side1.best_book || "novig"} size="lg" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] text-accent-value font-bold uppercase tracking-widest">The Play</span>
                  <span className="text-[10px] font-medium text-zinc-600">•</span>
                  <span className="text-[10px] text-zinc-400 font-medium tracking-wide">{getBookDisplayName(side1.best_book || "novig")}</span>
                </div>
                <div className={cn("text-base font-bold text-foreground drop-shadow-sm", practice ? "whitespace-normal break-words" : "truncate")}>
                  {formatClassicSideName(side1, market.market)} {formatClassicMarketTitle(market.market, market.market_arity)}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <div className="flex flex-col items-end gap-0.5">
                  <OddsBadge odds={side1.best_odds} highlight showTooltip betLink={side1.best_bet_link} onBetLinkClick={() => {
                    trackBetLinkClick(getWhaleWatchTrackingProps(opp, {
                      sportsbook: side1.best_book || "unknown",
                      odds_american: side1.best_odds,
                      ev_percentage: side1.edge_percent,
                      click_source: "odds_badge",
                    }));
                  }} />
                  <BookLiquidityLabel amount={playLiquidity} />
                </div>
                <div
                  className="flex min-h-8 min-w-[6.75rem] items-start justify-end"
                  data-testid="whale-play-history-slot"
                >
                  {openPlayHistory && (
                    <OddsHistoryTrigger
                      mode="inline"
                      label="Line history"
                      bookLabel={getBookDisplayName(side1.best_book || "novig")}
                      onOpen={openPlayHistory}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {practice && onPracticeBet ? (
          <div className="px-5 pb-4 shrink-0">
            <button
              data-practice-target="whale-bet"
              onClick={onPracticeBet}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-md btn-toplight bg-accent-value hover:bg-accent-value-strong transition-colors text-sm font-bold text-zinc-950"
            >
              Place Bet at {getBookDisplayName(side1.best_book || "novig")}
              <span className="font-black tabular-nums">{formatOdds(side1.best_odds)}</span>
              <ExternalLink size={14} className="text-zinc-950/60" />
            </button>
          </div>
        ) : !practice && side1.best_bet_link && (
          <div className="px-5 pb-4 shrink-0">
            <a
              href={side1.best_bet_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackBetLinkClick(getWhaleWatchTrackingProps(opp, {
                  sportsbook: side1.best_book || "unknown",
                  odds_american: side1.best_odds,
                  ev_percentage: side1.edge_percent,
                  click_source: "cta",
                }));
              }}
              className={cn(
                "flex items-center justify-center gap-2 w-full py-2.5 rounded-md btn-toplight",
                "bg-accent-value hover:bg-accent-value-strong transition-colors",
                "text-sm font-bold text-zinc-950"
              )}
            >
              Place Bet at {getBookDisplayName(side1.best_book || "novig")}
              <span className="font-black tabular-nums">{formatOdds(side1.best_odds)}</span>
              <ExternalLink size={14} className="text-zinc-950/60" />
            </a>
          </div>
        )}

        <div className="flex-1 overflow-auto px-5 py-4 scrollbar-custom scrollbar-color-primary">
          <div data-practice-target={practice ? "whale-depth" : undefined} className="mb-6">
            <ToolSectionHeader
              label="Whale Bets"
              rightSlot={(
                <div className="flex items-center gap-3 text-xs text-zinc-400">
                  <span className="tabular-nums">{formatOdds(avgOdds)} <span className="text-zinc-600">avg</span></span>
                  <span className="text-zinc-700">•</span>
                  <span className="tabular-nums">{formatCurrency(totalDepthLiquidity, true)} <span className="text-zinc-600">vol</span></span>
                </div>
              )}
            />

            <div className="space-y-0.5">
              {(() => {
                const { highlightModes, thresholdIndex } = computeDepthLevelHighlighting({
                  depthLevels: topDepthData,
                  edgePercent: side1.edge_percent,
                  retailOddsAmerican: side1.best_odds,
                  oppositeSideBooks: side2.books,
                  playBook: side1.best_book,
                  fairOutcomeShare: fairShare,
                });

                const elements: React.ReactNode[] = [];

                topDepthData.forEach((level, idx) => {
                  const levelKey = `${level.book}:${level.odds}`;

                  if (thresholdIndex !== null && idx === thresholdIndex) {
                    elements.push(
                      <EdgeBarDivider key="threshold" label="Break-even" />
                    );
                  }

                  elements.push(
                    <DepthBar
                      key={levelKey}
                      book={level.book}
                      odds={level.odds}
                      liquidity={level.liquidity}
                      maxLiquidity={maxLiquidity}
                      highlightMode={highlightModes[idx]}
                    />
                  );
                });

                return elements;
              })()}
            </div>
          </div>

          <div data-practice-target={practice ? "whale-where" : undefined} className="pt-3">
            <WhereToBetTable
              opp={opp}
              side1={side1}
              side2={side2}
              thirdSide={thirdSide}
              marketName={market.market}
              peerRows={peerRows}
              fairProbFor={fairProbFor}
              selectedBookKeys={selectedBookKeys}
              variant="desktop"
              openRowHistory={(row, isBestPrice) =>
                openHistoryFor(row.book, row.side1SelectionId, "desktop_peer_grid", {
                  oddsAmerican: row.side1Price?.odds,
                  betLinks: toBetLinkSet(row.side1BetLink, undefined, undefined) ?? undefined,
                  capacity: side1Capacity(row.book),
                  isBestPrice: isBestPrice ? true : undefined,
                })
              }
              emptyState={
                <div className="text-center text-muted-foreground py-8 text-sm">
                  No retail odds available
                </div>
              }
            />
          </div>

        </div>
      </div>
      {shareOpen && (
        <WhaleShareModal open onOpenChange={setShareOpen} opportunity={opp} classic />
      )}
      <OddsHistoryModal
        open={!!historyModal}
        onOpenChange={(o) => {
          if (!o) setHistoryModal(null);
        }}
        selection={historyModal?.selection ?? null}
        fallbackSeries={historyModal?.fallbackSeries}
        peers={historyModal?.peers}
      />
    </div>
  );
});
